import { Form, Formik } from "formik";
import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import { KC_Weights } from "../../../designsystem/ds_commons";
import { tt } from "../../../utils/i18n/i18n";
import KC_Input from "../../../designsystem/KC_FormInput/KC_Input";
import React, { useMemo } from "react";
import { getCountriesByLanguage } from "../../../utils/i18n/getCountriesByLang";
import KC_Button, {
  KC_ButtonSize
} from "../../../designsystem/KC_Button/KC_Button";
import { useDispatch } from "react-redux";
import { setLoading } from "../../../redux/actions/app";
import {
  updateCustomerThunk,
  useAccountToDisplaySelector
} from "../../../redux/actions/user";
import moment from "moment-timezone";
import BusinessUser from "@tootsweet/model/entities/BusinessUser";
import { CLIENTS_PER_DAY_ANSWER, SECTOR_LIST } from "../../../utils/constants";

interface Props {
  customer: BusinessUser | undefined;
}
export default function (props: Props) {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const handleSubmit = (values: BusinessUser) => {
    try {
      dispatch(setLoading(true));
      dispatch(
        updateCustomerThunk(
          {
            firstName: values.firstName,
            lastName: values.lastName,
            billingCountry: values.billingCountry,
            companyName: values.companyName,
            companyAddress: values.companyAddress,
            clientsPerDay: values.clientsPerDay,
            customerType: values.customerType
          },
          props.customer?.trackingID,
          accountToDisplay?.token
        )
      );
    } catch (err) {
      console.error(err);
      alert(tt("error_general_message"));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const { ALL_COUNTRIES_NAME, ALL_COUNTRIES_CODE } = useMemo(() => {
    return getCountriesByLanguage();
  }, []);

  const timezones = useMemo(() => {
    return moment.tz.names();
  }, []);

  if (!props.customer) {
    return null;
  }

  return (
    <Formik<BusinessUser>
      key={props.customer.trackingID}
      initialValues={props.customer}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, setFieldValue, dirty }) => {
        return (
          <Form id="bu-form" className="h-100" autoComplete="off">
            <div className="w-100 d-flex flex-column st-gap h-100">
              <div>
                <label htmlFor={"firstName"}>
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("jp_onboarding_form_label_first_name")}
                  />
                </label>
                <KC_Input
                  id="firstName"
                  htmlFor="firstName"
                  placeholder={tt("jp_onboarding_form_label_first_name")}
                  type="text"
                  setFieldValue={setFieldValue}
                  required={true}
                  value={values?.firstName ? values.firstName : ""}
                  disabled={false}
                />
              </div>
              <div>
                <label htmlFor={"lastName"}>
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("jp_onboarding_form_label_last_name")}
                  />
                </label>
                <KC_Input
                  id="lastName"
                  htmlFor="lastName"
                  placeholder={tt("jp_onboarding_form_label_last_name")}
                  type="text"
                  setFieldValue={setFieldValue}
                  required={true}
                  value={values?.lastName ? values.lastName : ""}
                  disabled={false}
                />
              </div>
              <div>
                <label htmlFor={"contactEmail"}>
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("jp_onboarding_form_label_email")}
                  />
                </label>
                <KC_Input
                  id="contactEmail"
                  htmlFor="contactEmail"
                  placeholder={tt("jp_onboarding_form_label_email")}
                  type="text"
                  setFieldValue={setFieldValue}
                  required={true}
                  value={values?.contactEmail ? values.contactEmail : ""}
                  disabled={false}
                />
              </div>
              <div>
                <KC_UIText
                  weight={KC_Weights.bold}
                  text={tt("profile_company_name")}
                />
                <KC_Input
                  id="companyName"
                  setFieldValue={setFieldValue}
                  htmlFor={"companyName"}
                  value={values.companyName || ""}
                />
              </div>
              <div>
                <label>
                  <KC_UIText
                    weight={KC_Weights.bold}
                    text={tt("profile_billing_country")}
                  />
                </label>
                <div className="kc-input-wrapper">
                  <select
                    id="billingCountry"
                    className="kc_input pl-3"
                    required={true}
                    value={
                      values.billingCountry
                        ? values.billingCountry?.toUpperCase()
                        : ""
                    }
                    onChange={(e) => {
                      void setFieldValue("billingCountry", e.target.value);
                    }}
                  >
                    <option>{tt("profile_billing_country")}</option>
                    {ALL_COUNTRIES_NAME.map((name, idx) => {
                      const val = ALL_COUNTRIES_CODE[idx];
                      return (
                        <option key={`country-${idx}-${val}`} value={val}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div>
                <label>
                  <KC_UIText
                    weight={KC_Weights.bold}
                    text={tt("billing_address_label")}
                  />
                </label>
                <KC_Input
                  id="companyAddress"
                  htmlFor="companyAddress"
                  type="text"
                  setFieldValue={setFieldValue}
                  required={true}
                  value={values?.companyAddress ? values.companyAddress : ""}
                />
              </div>
              <div>
                <label htmlFor={"sector"}>
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("jp_onboarding_form_label_sector")}
                  />
                </label>
                <div className="kc-input-wrapper">
                  <select
                    id="onboarding-sector"
                    onChange={(e) => {
                      setFieldValue("customerType", e.target.value);
                    }}
                    value={values.customerType}
                    className="kc_input px-2"
                    required={true}
                  >
                    <option value=""></option>
                    {SECTOR_LIST.map((sector, idx) => {
                      return (
                        <option key={sector.value + idx} value={sector.value}>
                          {sector.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor={"clientsPerDay"}>
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("jp_onboarding_form_label_clients_per_day")}
                  />
                </label>
                <div className="kc-input-wrapper">
                  <select
                    id="clientsPerDay"
                    onChange={(e) => {
                      setFieldValue("clientsPerDay", e.target.value);
                    }}
                    value={values.clientsPerDay}
                    className="kc_input px-2"
                    required
                  >
                    <option value=""></option>
                    {CLIENTS_PER_DAY_ANSWER.map((answer, idx) => {
                      return (
                        <option key={answer.name + idx} value={answer.name}>
                          {answer.displayName}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div>
                <label>
                  <KC_UIText
                    weight={KC_Weights.bold}
                    text={tt("profile_timezone")}
                  />
                </label>
                <div className="kc-input-wrapper">
                  <select
                    id="tz"
                    className="kc_input pl-3"
                    disabled={true}
                    value={values.tz || "Europe/Paris"}
                  >
                    {timezones.map((tz) => {
                      return (
                        <option key={`timezone-${tz}`} value={tz}>
                          {tz}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div className="kc-input-wrapper">
                <small>{props.customer?.trackingID}</small>
              </div>
              <div className="flex-grow-1" />
              <KC_Button
                id="save"
                type="submit"
                text={tt("form_button_save")}
                disabled={!dirty}
                size={KC_ButtonSize.Large}
                className="d-flex"
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
